import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { FaTrash, FaMinus, FaPlus, FaShoppingBag, FaArrowRight } from 'react-icons/fa';
import { formatCurrency } from '../utils/currency';
import './Cart.css';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { settings } = useSettings();
  const navigate = useNavigate();

  const subtotal = getCartTotal();
  const taxRate = (settings?.taxRate || 0) / 100;
  const tax = subtotal * taxRate;
  const freeShipThreshold = settings?.freeShippingThreshold || 0;
  const shippingFee = settings?.shippingFee || 0;
  const shipping = freeShipThreshold > 0 && subtotal >= freeShipThreshold ? 0 : shippingFee;
  const total = subtotal + tax + shipping;

  const handleCheckout = () => {
    if (!user) navigate('/login', { state: { from: '/checkout' } });
    else navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page page">
        <div className="container">
          <div className="empty-state">
            <FaShoppingBag className="empty-state-icon" />
            <h3>Your cart is empty</h3>
            <p>Looks like you haven't added anything yet. Let's fix that.</p>
            <Link to="/products" className="btn btn-primary btn-lg">
              Start shopping <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page page">
      <div className="container">
        <div className="cart-header">
          <h1>Shopping cart</h1>
          <p className="page-subtitle">{cart.length} {cart.length === 1 ? 'item' : 'items'} in your cart</p>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cart.map((item) => (
              <div key={item._id} className="cart-item">
                <Link to={`/products/${item._id}`} className="item-image">
                  {item.images && item.images[0] ? (
                    <img src={item.images[0].url} alt={item.name} />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                </Link>

                <div className="item-content">
                  <div className="item-info">
                    <Link to={`/products/${item._id}`}>
                      <h3>{item.name}</h3>
                    </Link>
                    <p className="item-price">{formatCurrency(item.price, settings?.currency)}</p>
                  </div>

                  <div className="item-controls">
                    <div className="quantity-stepper sm">
                      <button onClick={() => updateQuantity(item._id, item.quantity - 1)}>
                        <FaMinus />
                      </button>
                      <input type="text" value={item.quantity} readOnly />
                      <button onClick={() => updateQuantity(item._id, item.quantity + 1)}>
                        <FaPlus />
                      </button>
                    </div>

                    <div className="item-line-total">
                      {formatCurrency(item.price * item.quantity, settings?.currency)}
                    </div>

                    <button
                      className="item-remove"
                      onClick={() => removeFromCart(item._id)}
                      aria-label="Remove"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="cart-summary">
            <h2>Order summary</h2>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal, settings?.currency)}</span>
            </div>
            {tax > 0 && (
              <div className="summary-row">
                <span>Tax ({settings?.taxRate}%)</span>
                <span>{formatCurrency(tax, settings?.currency)}</span>
              </div>
            )}
            <div className="summary-row">
              <span>Shipping</span>
              <span>{shipping === 0 ? <span className="free-tag">Free</span> : formatCurrency(shipping, settings?.currency)}</span>
            </div>

            {freeShipThreshold > 0 && shipping > 0 && (
              <div className="ship-progress">
                <div className="ship-progress-bar">
                  <div
                    className="ship-progress-fill"
                    style={{ width: `${Math.min(100, (subtotal / freeShipThreshold) * 100)}%` }}
                  />
                </div>
                <small>
                  Add {formatCurrency(freeShipThreshold - subtotal, settings?.currency)} more for free shipping
                </small>
              </div>
            )}

            <div className="summary-divider" />

            <div className="summary-row total">
              <span>Total</span>
              <span>{formatCurrency(total, settings?.currency)}</span>
            </div>

            <button onClick={handleCheckout} className="btn btn-primary btn-block btn-lg">
              Proceed to checkout <FaArrowRight />
            </button>

            <Link to="/products" className="continue-shopping">
              Continue shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Cart;
